import Cookies from 'js-cookie';
import { memo, useEffect, useMemo, useRef } from 'react';

import { useHitSwatches } from '@hooks/useHitSwatches';

import { pushProductClickToDataLayer } from '@utils/analytics/plpAndSearchDataLayer';
import { ALGOLIA_CHECKOUT_QUERY_ID_NAME } from '@adapters/bigcommerce/constants';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { trackProductEvent } from '@utils/gtm/ga4';
import { GA4_EVENT_NAMES } from '@utils/gtm/ga4/constants';
import {
  createDataLayerProduct,
  getList,
} from '@utils/analytics/plpAndSearchDataLayer';
import ProductCard from '_components/ProductStrip/ProductCard';
import { getDisplayTags } from '_components/Product/ProductMetaInfo/ProductMetaTags/ProductMetaTags';
import { useTranslation } from '@i18n/next-i18next-shim';
import QuickAdd from '_components/QuickAdd';
import { FEATURE_FLAG_QUICK_ADD } from '@config/feature-flag';
import { isGiftCardHit } from '@utils/algolia/isGiftCardHit';

import type {
  PriceGroups,
  HitVariant,
  HitVariants,
  HitProps,
  SendEventType,
} from '@components/algolia/Hit';

export type { PriceGroups, HitVariant, HitVariants, HitProps, SendEventType };

interface Props {
  hit: HitProps;
  locale?: string;
  isPriority?: boolean;
}

const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  const prevHit = prevProps.hit;
  const nextHit = nextProps.hit;

  const arePriceRangesEqual = (
    prevPriceRange: HitVariant['priceRange'],
    nextPriceRange: HitVariant['priceRange']
  ) => {
    if (!prevPriceRange && !nextPriceRange) {
      return true;
    }
    if (!prevPriceRange || !nextPriceRange) {
      return false;
    }

    const areArraysEqual = (
      prevArray: [number, number] | undefined,
      nextArray: [number, number] | undefined
    ) => {
      if (!prevArray && !nextArray) {
        return true;
      }
      if (!prevArray || !nextArray) {
        return false;
      }
      return prevArray[0] === nextArray[0] && prevArray[1] === nextArray[1];
    };

    return (
      areArraysEqual(prevPriceRange.price, nextPriceRange.price) &&
      areArraysEqual(prevPriceRange.salePrice, nextPriceRange.salePrice)
    );
  };

  const areVariantsEqual = (
    prevVariants: HitVariants,
    nextVariants: HitVariants
  ) => {
    const prevVariantKeys = Object.keys(prevVariants);
    const nextVariantKeys = Object.keys(nextVariants);

    if (prevVariantKeys.length !== nextVariantKeys.length) {
      return false;
    }

    return prevVariantKeys.every((key) => {
      const prevVariant = prevVariants[key];
      const nextVariant = nextVariants[key];

      return (
        prevVariant.swatch === nextVariant.swatch &&
        prevVariant.image === nextVariant.image &&
        prevVariant.price === nextVariant.price &&
        prevVariant.salePrice === nextVariant.salePrice &&
        prevVariant.sku === nextVariant.sku &&
        prevVariant.availableOnlinePurchase ===
          nextVariant.availableOnlinePurchase &&
        arePriceRangesEqual(prevVariant.priceRange, nextVariant.priceRange)
      );
    });
  };

  return (
    prevHit.objectID === nextHit.objectID &&
    prevHit.image === nextHit.image &&
    prevHit.__queryID === nextHit.__queryID &&
    prevHit.url === nextHit.url &&
    prevHit.title === nextHit.title &&
    prevHit.reviewScore === nextHit.reviewScore &&
    prevHit.reviewCount === nextHit.reviewCount &&
    prevHit.parentSku === nextHit.parentSku &&
    (prevHit.tags ?? []).length === (nextHit.tags ?? []).length &&
    (prevHit.tags ?? []).every(
      (tag, index) => tag === (nextHit.tags ?? [])[index]
    ) &&
    areVariantsEqual(prevHit.variants, nextHit.variants)
  );
};

const Hit = memo(
  ({
    hit,
    locale,
    isPriority = false,
    sendEvent,
  }: {
    hit: HitProps;
    locale?: string;
    isPriority?: boolean;
    sendEvent: SendEventType;
  }) => {
    const { t } = useTranslation('searchResults');

    const dealsBannerRef = useRef<HTMLDivElement>(null);
    const { activeVariant, swatches, prices, priceGroupKey } = useHitSwatches(
      hit.colour,
      hit.variants,
      {
        price: hit.price,
        salePrice: hit.salePrice,
        image: hit.image,
        imageTransparent: hit.imageTransparent,
        badge: hit.badge ?? null,
        badgeGuest: hit.badgeGuest ?? null,
        sku: hit.sku,
      },
      locale
    );

    const algoliaDebug = Cookies.get('algoliaDebug');
    if (algoliaDebug && typeof hit.colour === 'undefined') {
      console.log('hit.colour is undefined', { hit });
    }

    if (algoliaDebug && !activeVariant) {
      console.log('---- no active variant', {
        hitProps: hit,
        activeVariant,
        swatches,
      });
    }

    const hasMemberPriceDifference = activeVariant?.hasMemberPriceDifference;

    const { VIEW_ITEM_LIST } = GA4_EVENT_NAMES;

    const isVisible = useObjectVisibility(dealsBannerRef);

    useEffect(() => {
      if (hasMemberPriceDifference && isVisible) {
        trackProductEvent({
          eventName: VIEW_ITEM_LIST,
          products: [createDataLayerProduct(hit, activeVariant)],
          itemListName: getList(),
        });
      }
    }, [
      hit,
      activeVariant,
      hasMemberPriceDifference,
      isVisible,
      VIEW_ITEM_LIST,
    ]);

    const rawBadge =
      hit.isMemberPrice && priceGroupKey !== 'guest'
        ? activeVariant?.badge
        : activeVariant?.badgeGuest;

    if (
      algoliaDebug &&
      (rawBadge?.code === 'CLEARANCE' || rawBadge?.code === 'SALE') &&
      !activeVariant?.salePrice
    ) {
      console.log('!!! note: sale item does not have sale price <---');
      console.log({ activeVariant });
      console.log({ hit });
    }

    const activeColourKey = activeVariant?.swatchName ?? hit.colour;
    const activeColourVariant = hit.variants?.[activeColourKey];
    const activeColourAvailableOnlinePurchase =
      activeColourVariant?.availableOnlinePurchase;

    const variantQuerySlug = activeVariant?.colourQuerySlug;

    const queryString = new URLSearchParams(
      `${variantQuerySlug ? variantQuerySlug : ''}`
    ).toString();

    const hitUrl = `${hit.url}${
      queryString.length > 0 ? `?${queryString}` : ''
    }`;
    const hitHasSalePrice =
      activeVariant?.salePriceFormatted || activeVariant?.priceRange?.salePrice;

    const handleHitClick = (clickedHit: HitProps) => {
      Cookies.set(ALGOLIA_CHECKOUT_QUERY_ID_NAME, clickedHit.__queryID || '');
      pushProductClickToDataLayer(clickedHit, activeVariant);
      sendEvent('click', clickedHit, 'Product Clicked');
    };
    const showNewTag = hit?.isNew;

    const displayTags = useMemo(
      () => [
        ...(showNewTag
          ? [
              {
                normalizedSource: 'promo-new',
                name: t('product.badge.new'),
                color: 'blue' as const,
              },
            ]
          : []),
        ...getDisplayTags(hit?.tags),
      ],
      [showNewTag, t, hit?.tags]
    );

    const hasMemberPriceDifferencePlp =
      prices?.member?.price !== prices?.guest?.price ||
      prices?.member?.salePrice !== prices?.guest?.salePrice;

    const hasClearancePrice = rawBadge?.code === 'CLEARANCE';

    const isOnSaleOrClearance = Boolean(hitHasSalePrice) || hasClearancePrice;

    const showMemberPricingDealsBanner =
      hasMemberPriceDifferencePlp && !hasClearancePrice;

    const hasGroupPriceDifference =
      activeVariant?.hasCustomerGroupPriceDifference &&
      priceGroupKey !== 'member';

    // Only worth showing the member-vs-guest comparison when the viewer is on
    // member pricing and the two prices actually differ.
    const showMemberPriceComparison =
      priceGroupKey === 'member' && hasMemberPriceDifferencePlp;

    const showQuickAdd =
      FEATURE_FLAG_QUICK_ADD &&
      hit.availableOnPlp !== false &&
      activeColourAvailableOnlinePurchase !== false &&
      !isGiftCardHit(hit.parentSku);

    return (
      <ProductCard
        priority={isPriority}
        productImage={
          activeVariant?.imageTransparent ||
          activeVariant?.image ||
          hit?.imageTransparent ||
          hit?.image ||
          ''
        }
        brand={hit?.brand || ''}
        displayName={hit?.title || ''}
        rating={hit?.reviewScore?.toString() || ''}
        reviews={hit?.reviewCount?.toString() || ''}
        productUrl={hitUrl}
        guestDisplayPrice={activeVariant?.guestPriceFormatted || ''}
        displayPrice={activeVariant?.priceFormatted || ''}
        salePrice={
          activeVariant?.priceRange?.salePrice ||
          activeVariant?.salePriceFormatted ||
          activeVariant?.priceFormatted ||
          ''
        }
        swatches={swatches}
        trackClick={() => handleHitClick(hit)}
        tags={displayTags}
        isCanadianBrand={hit?.isCanadianBrand}
        isMadeInCanada={hit?.isMadeInCanada}
        productCode={hit?.parentSku}
        showMemberPricingDealsBanner={showMemberPricingDealsBanner}
        hasGroupPriceDifference={hasGroupPriceDifference}
        isOnSaleOrClearance={isOnSaleOrClearance}
        showMemberPriceComparison={showMemberPriceComparison}
        dealsBannerRef={dealsBannerRef}
        quickAdd={
          showQuickAdd ? (
            <QuickAdd
              productCode={hit.parentSku}
              productTitle={hit.title}
              productUrl={hitUrl}
              initialColour={{
                colourName: activeColourKey,
                colourSku: activeVariant?.sku ?? hit.sku,
              }}
              analytics={{ queryID: hit.__queryID }}
              variantCount={activeColourVariant?.variantCount}
            />
          ) : null
        }
      />
    );
  },
  arePropsEqual
);

export default Hit;
